import { apiSlice } from 'store/slices/api';
import {
  ClientV2DELETEResponse,
  ClientV2PATCHResponse,
  ClientV2POSTResponse,
} from 'types/api';
import {
  StockLotSchema,
  StockLotPATCHSchema,
  ReceivePurchaseLinePOSTSchema,
  ReceiveShipmentLinePOSTSchema,
  CreateReceiveShipmentLinePOSTSchema,
  PartialSplitStockLotPurchaseLinePOSTSchema,
} from 'types/clientV2/stock';
import { CLIENT_V2_URLS } from 'store/slices/constants/clientV2';

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    updateStockLot: builder.mutation<
      ClientV2PATCHResponse<StockLotSchema>,
      {
        id: StockLotSchema['id'];
        payload: StockLotPATCHSchema;
      }
    >({
      query: ({ id, payload }) => ({
        url: CLIENT_V2_URLS.STOCK_LOT(id),
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'StockLot', id },
        'PreviewZpl',
        { type: 'HandlingTaskRows', id: 'LIST' }, // Temporarily included for backwards compatibility
        { type: 'ClientV2StockEvents', id: 'LIST' },
      ],
    }),
    deleteStockLot: builder.mutation<
      ClientV2DELETEResponse<StockLotSchema>,
      // Note: not ClientV2DELETERequest because flagship didn't have a StockLotsClientView only StockLotClientView, and I didn't want to build it from scratch for the needed use case
      {
        id: StockLotSchema['id'];
      }
    >({
      query: ({ id }) => ({
        url: CLIENT_V2_URLS.STOCK_LOT(id),
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'StockLot', id: 'LIST' },
        { type: 'StockLot', id: 'PARTIAL-LIST' },
        { type: 'ClientV2StockEvents', id: 'LIST' },
        { type: 'ClientV2ShipmentLines', id: 'LIST' },
      ],
    }),
    receivePurchaseLineV2: builder.mutation<
      ClientV2POSTResponse<StockLotSchema>,
      {
        id: string;
        payload: ReceivePurchaseLinePOSTSchema;
      }
    >({
      query: ({ id, payload }) => ({
        url: CLIENT_V2_URLS.RECEIVE_STOCK_LOT_BY_PURCHASE_LINE(id),
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [
        { type: 'ClientV2StockEvents', id: 'LIST' },
        { type: 'ClientV2HandlingRequests', id: 'LIST' },
        { type: 'HandlingRequests', id: 'LIST' }, // Temporarily included for backwards compatibility
        { type: 'HandlingTaskRows', id: 'LIST' }, // Temporarily included for backwards compatibility
        { type: 'StockLot', id: 'LIST' },
        { type: 'StockLot', id: 'PARTIAL-LIST' },
        { type: 'ExpandedPurchaseLines', id: 'LIST' },
        { type: 'ExpandedPurchaseLines', id: 'PARTIAL-LIST' },
        { type: 'ConfirmedPurchaseLines', id: 'LIST' },
        { type: 'ConfirmedPurchaseLines', id: 'PARTIAL-LIST' },
        { type: 'ClientV2PurchaseOrderLines', id: 'LIST' },
        { type: 'ClientV2PurchaseOrderLines', id: 'PARTIAL-LIST' },
      ],
    }),
    receiveShipmentLineV2: builder.mutation<
      ClientV2POSTResponse<StockLotSchema>,
      {
        id: string;
        payload: ReceiveShipmentLinePOSTSchema;
      }
    >({
      query: ({ id, payload }) => ({
        url: CLIENT_V2_URLS.RECEIVE_STOCK_LOT_BY_SHIPMENT_LINE(id),
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [
        { type: 'ClientV2StockEvents', id: 'LIST' },
        { type: 'ClientV2HandlingRequests', id: 'LIST' },
        { type: 'HandlingRequests', id: 'LIST' }, // Temporarily included for backwards compatibility
        { type: 'HandlingTaskRows', id: 'LIST' }, // Temporarily included for backwards compatibility
        { type: 'StockLot', id: 'LIST' },
        { type: 'StockLot', id: 'PARTIAL-LIST' },
        { type: 'Shipments', id: 'LIST' }, // Temporarily included for backwards compatibility
      ],
    }),
    createReceiveShipmentLine: builder.mutation<
      ClientV2POSTResponse<StockLotSchema>,
      CreateReceiveShipmentLinePOSTSchema
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.CREATE_RECEIVE_SHIPMENT_LINE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'ClientV2StockEvents', id: 'LIST' },
        { type: 'ClientV2HandlingRequests', id: 'LIST' },
        { type: 'HandlingRequests', id: 'LIST' }, // Temporarily included for backwards compatibility
        { type: 'HandlingTaskRows', id: 'LIST' }, // Temporarily included for backwards compatibility
        { type: 'StockLot', id: 'LIST' },
        { type: 'StockLot', id: 'PARTIAL-LIST' },
        { type: 'Shipments', id: 'LIST' }, // Temporarily included for backwards compatibility
      ],
    }),
    partialSplitStockLotPurchaseLine: builder.mutation<
      ClientV2POSTResponse<StockLotSchema>,
      {
        id: string;
        payload: PartialSplitStockLotPurchaseLinePOSTSchema;
      }
    >({
      query: ({ id, payload }) => ({
        url: CLIENT_V2_URLS.PARTIAL_SPLIT_STOCK_LOT_PURCHASE_LINE(id),
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'StockLot', id },
        { type: 'StockLot', id: 'LIST' },
        { type: 'StockLot', id: 'PARTIAL-LIST' },
        { type: 'ExpandedPurchaseLines', id: 'LIST' },
        { type: 'ExpandedPurchaseLines', id: 'PARTIAL-LIST' },
        { type: 'ConfirmedPurchaseLines', id: 'LIST' },
        { type: 'ConfirmedPurchaseLines', id: 'PARTIAL-LIST' },
      ],
    }),
  }),
});

export const {
  useUpdateStockLotMutation,
  useReceivePurchaseLineV2Mutation,
  useReceiveShipmentLineV2Mutation,
  useCreateReceiveShipmentLineMutation,
  useDeleteStockLotMutation,
  usePartialSplitStockLotPurchaseLineMutation,
} = extendedApiSlice;
